import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { socialApi } from "@bw/shared/api";
import {
  approveCommunityRequest,
  declineCommunityRequest,
  listCommunityAdmissions,
  revokeCommunityInvitation,
  createCommunityInvitation,
} from "@/libs/communityApi";
import type { CommunityAdmissionListResponse } from "@/libs/communityApi";

function admissionsKey(communityId: string) {
  return ["community-admissions", communityId, "pending"] as const;
}

/** Business owner/moderator review over the persisted admission state machine. */
export function useCommunityAdmissions(communityId: string) {
  const queryClient = useQueryClient();
  const query = useQuery<CommunityAdmissionListResponse>({
    queryKey: admissionsKey(communityId),
    queryFn: () => listCommunityAdmissions(communityId, { status: "pending" }),
    enabled: Boolean(communityId),
  });
  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: admissionsKey(communityId),
    });
    void queryClient.invalidateQueries({
      queryKey: ["facility-community", communityId],
    });
  };
  const approve = useMutation({
    mutationFn: (admissionId: string) => approveCommunityRequest(admissionId),
    onSuccess: invalidate,
  });
  const decline = useMutation({
    mutationFn: (admissionId: string) => declineCommunityRequest(admissionId),
    onSuccess: invalidate,
  });
  const revoke = useMutation({
    mutationFn: (admissionId: string) => revokeCommunityInvitation(admissionId),
    onSuccess: invalidate,
  });
  const invite = useMutation({
    mutationFn: (userId: string) =>
      createCommunityInvitation(communityId, { user_id: userId }),
    onSuccess: invalidate,
  });
  return { query, approve, decline, revoke, invite };
}

export function useCommunityInviteCandidates(query: string, enabled: boolean) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ["community-admission-candidates", trimmed],
    queryFn: () => socialApi.searchPlayers(trimmed, "all", { limit: 20 }),
    enabled: enabled && trimmed.length >= 2,
  });
}
